import { useEffect, useState } from "react";
import type { Nav } from "../nav";
import { deleteAccount, fetchAccount, getGuestId, osLabel, signInAvailable, signOutEverywhere, type AccountInfo } from "../account";
import { BackButton, Sheet, Spinner, TabBar, useApp, useHubSummaries } from "../ui";
import { useComputerAccounts, useSigned } from "./SignIn";
import { RemoteAvailability } from "./RemoteAvailability";
import { AlertsSetting } from "./Alerts";
import { VoiceSetting } from "./Voice";
import { ARCHIVE_OPTIONS, setArchiveDays, useArchiveDays } from "../archive";
import { Chevron, Tile, YouIcon } from "./YouParts";

type Confirm = null | "signout" | "delete";


/** Who you are, what it unlocks, which computers share it, and the ways out (sign out, delete). */
export function AccountScreen({ nav }: { nav: Nav }) {
  const { hub, store, toast } = useApp();
  const signed = useSigned();
  const archiveDays = useArchiveDays();
  const summaries = useHubSummaries(hub);
  const computerStates = useComputerAccounts();
  const [info, setInfo] = useState<AccountInfo | null>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setInfo(null);
    if (signed) void fetchAccount().then(setInfo);
  }, [signed]);

  // each computer's own word about its sign-in, in the order the home screen lists them
  const rows = summaries.map((s) => {
    const a = hub.get(s.computer.id)?.getSnapshot().account;
    return { id: s.computer.id, label: store.get(s.computer.id)?.label ?? "Computer", state: s.state, signedIn: a?.signedIn ?? null, hint: a?.hint ?? null };
  });
  const signedComputers = rows.filter((r) => r.signedIn).length;
  const linkedWithoutPhone = !signed && computerStates.some((c) => c.signedIn);

  // from the You tab there is no screen behind this one: go to Home, which is where a guest starts
  const leave = () => (nav.isRoot ? nav.goTab({ name: "home" }) : nav.back());
  const run = async (what: "signout" | "delete") => {
    setBusy(true);
    setError(null);
    if (what === "signout") {
      await signOutEverywhere(hub);
      setBusy(false);
      setConfirm(null);
      toast("Signed out");
      leave();
      return;
    }
    const r = await deleteAccount();
    setBusy(false);
    if (!r.ok) return setError(r.message);
    setConfirm(null);
    toast("Account deleted");
    leave();
  };

  const initial = (signed?.name ?? signed?.email ?? "G").trim().charAt(0).toUpperCase();
  return (
    <div className="screen" data-screen="account">
      <div className="safe-top">
        <div className={"topbar" + (nav.isRoot ? " root" : "")}>
          {!nav.isRoot && <BackButton onClick={nav.back} />}
          <div className="title-col">
            <div className="t">{nav.isRoot ? "Settings" : "Account"}</div>
            {nav.isRoot && <div className="sub">Your agents, alerts and account</div>}
          </div>
        </div>
      </div>
      <div className="scroll">
        <div className="content you-page">
          <div className="you-hero card">
            <span className={"avatar" + (signed ? "" : " guest")} aria-hidden>
              {signed ? initial : "G"}
            </span>
            <span className="you-who">
              <span className="you-name">{signed ? (signed.name ?? signed.email) : "Guest"}</span>
              <span className="you-sub">{signed ? (signed.name ? signed.email : "Signed in") : `${getGuestId()} · only on this phone`}</span>
              <span className="plan-badge">{signed ? "Early access from ur bro 😉" : "Early access"}</span>
            </span>
          </div>

          {!signed && linkedWithoutPhone && (
            <div className="card acct-note">
              <strong>Your computer is linked to an account, this phone isn't.</strong>
              <span>Sign in on this phone too, to use the same account here.</span>
            </div>
          )}
          {!signed && signInAvailable() && (
            <button className="btn primary" onClick={() => nav.push({ name: "signin", then: { name: "account" } })}>
              Sign in with Google
            </button>
          )}

          <section aria-labelledby="you-agents">
            <div className="group-label" id="you-agents">Agents</div>
            <div className="card you-list">
              <button className="you-row" onClick={() => nav.push({ name: "rules" })} data-testid="open-rules">
                <Tile>{YouIcon.shield}</Tile>
                <span className="you-row-main">
                  <strong>Approval rules</strong>
                  <span>Always allow or never allow a command. Kept on your computer.</span>
                </span>
                <Chevron />
              </button>
              <button className="you-row" onClick={() => nav.push({ name: "skills" })} data-testid="open-skills">
                <Tile>{YouIcon.skills}</Tile>
                <span className="you-row-main">
                  <strong>Skills</strong>
                  <span>One library for Claude Code, Codex and Antigravity. Add skills from GitHub.</span>
                </span>
                <Chevron />
              </button>
              <button className="you-row" onClick={() => nav.push({ name: "usage" })} data-testid="open-usage">
                <Tile>{YouIcon.gauge}</Tile>
                <span className="you-row-main">
                  <strong>Usage</strong>
                  <span>How much of your Claude, Codex and Antigravity plans is used.</span>
                </span>
                <Chevron />
              </button>
            </div>
          </section>

          <section aria-labelledby="you-archive">
            <div className="group-label" id="you-archive">Sessions</div>
            <div className="card you-list">
              <div className="you-row static archive-row">
                <Tile>{YouIcon.archive}</Tile>
                <span className="you-row-main">
                  <strong>Archive old sessions</strong>
                  <span>Sessions quiet for longer than this move to Archived on Home, so the list stays short.</span>
                  <span className="agent-seg archive-seg" role="tablist" aria-label="Archive after">
                    {ARCHIVE_OPTIONS.map((o) => (
                      <button key={o.days} role="tab" aria-selected={archiveDays === o.days} className={archiveDays === o.days ? "on" : ""} onClick={() => setArchiveDays(o.days)} data-testid={`archive-${o.days}`} aria-label={o.label}>
                        {o.short}
                      </button>
                    ))}
                  </span>
                </span>
              </div>
            </div>
          </section>

          <AlertsSetting />
          <VoiceSetting />
          <RemoteAvailability />

          <section aria-labelledby="you-help">
            <div className="group-label" id="you-help">Help &amp; feedback</div>
            <div className="card you-list">
              <button className="you-row" onClick={() => nav.push({ name: "support" })} data-testid="report-problem">
                <Tile>{YouIcon.help}</Tile>
                <span className="you-row-main">
                  <strong>Report a problem</strong>
                  <span>Something broke or felt wrong? Tell us.</span>
                </span>
                <Chevron />
              </button>
            </div>
          </section>


          {signed && (
            <section aria-labelledby="you-account">
              <div className="group-label" id="you-account">Account</div>
              <div className="card you-list">
                <button className="you-row" onClick={() => setConfirm("signout")}>
                  <Tile>{YouIcon.signout}</Tile>
                  <span className="you-row-main">
                    <strong>Sign out</strong>
                  </span>
                  <Chevron />
                </button>
                <button className="you-row danger" onClick={() => setConfirm("delete")}>
                  <Tile>{YouIcon.trash}</Tile>
                  <span className="you-row-main">
                    <strong>Delete account</strong>
                  </span>
                  <Chevron />
                </button>
              </div>
              <div className="fine">Signing out removes the computers from this phone. It never deletes anything on them.</div>
            </section>
          )}
        </div>
      </div>
      {nav.isRoot && <TabBar nav={nav} />}

      {confirm && (
        <Sheet onClose={() => !busy && setConfirm(null)}>
          <div className="head">
            <span className="t">{confirm === "signout" ? "Sign out of Brocoded?" : "Delete your account?"}</span>
          </div>
          <div className="copy" style={{ lineHeight: 1.45 }}>
            {confirm === "signout"
              ? `This phone${signedComputers > 0 ? ` and ${signedComputers} signed-in computer${signedComputers === 1 ? "" : "s"}` : ""} go back to guest, and your computers are removed from this phone. Sessions and chats stay on the computers; to use one again, sign in and scan its QR code.`
              : "Your account and its links to your computers are removed from Brocoded. Sessions and chats on your computers stay, and everything goes back to guest."}
          </div>
          {error && (
            <div className="error-box" role="alert">
              <div className="m">{error}</div>
            </div>
          )}
          <div className="stack">
            <button className={"btn " + (confirm === "delete" ? "danger" : "primary")} disabled={busy} onClick={() => void run(confirm)}>
              {busy && <Spinner />}
              {confirm === "signout" ? "Sign out" : "Delete account"}
            </button>
            <button className="btn secondary" disabled={busy} onClick={() => setConfirm(null)}>
              Cancel
            </button>
          </div>
        </Sheet>
      )}
    </div>
  );
}
